import { without } from "../fields";
import { hexDistance } from "../hex";
import { unitById } from "../run/board";
import { isSeatUnit, seatAt, seatOfUnit, seatUnit, soldiersOwnUnit, withSeat } from "../run/seats";
import { sameOwner } from "../run/sides";
import type { FactionId, Role, RunState, SeatState, Unit } from "../types/state";
import { isBroken } from "../units/morale";

/**
 * Company (C2): the soldier at work and on duty, a comrade beside it, enlisting with a
 * commander's squad, and discharge.
 */

/**
 * Whether a soldier seat's unit is at work (C1): alive in this unit and sent somewhere by its own
 * seat at least once in this life. One that was never given an order earns nothing, so a
 * human away from the keyboard is paid for nothing, not even the gate it spawned on.
 */
export function isAtWork(state: RunState, unit: Unit): boolean {
  return isSeatUnit(state, unit.id) && unit.orderedBy === "soldier";
}

/**
 * Whether a comrade stands beside the unit (C2): another unit of its kingdom on a neighbouring
 * hex, unbroken. The company the courage rule reads, less the broken.
 */
export function comradeBeside(state: RunState, unit: Unit): boolean {
  return state.units.some(
    (other) =>
      other.owner === unit.owner &&
      other.id !== unit.id &&
      other.brokenTick === undefined &&
      hexDistance(other.coord, unit.coord) === 1,
  );
}

/** The commander the seat fell in with (C2), while he lives and leads; undefined otherwise. */
export function enlistedWith(state: RunState, seat: SeatState | undefined): Unit | undefined {
  const id = seat?.enlisted;
  const leader = id === undefined ? undefined : unitById(state, id);
  return leader?.commander && leader.owner === seat?.faction ? leader : undefined;
}

/**
 * A soldier seat's unit falls in with a commander (C2), by its own verb and nobody else's (D8):
 * beside him, unbroken, a living commander of its kingdom. A second enlistment replaces the
 * first. Same object back when refused or when it already serves him.
 */
export function enlistWith(
  state: RunState,
  faction: FactionId,
  unitId: number,
  commanderId: number,
  issuer: Role,
): RunState {
  const unit = soldiersOwnUnit(state, faction, unitId, issuer);
  const seat = seatOfUnit(state, unitId);
  const leader = unitById(state, commanderId);
  if (
    !unit ||
    !seat ||
    !state.rules.company ||
    isBroken(state, unit) ||
    !leader?.commander ||
    !sameOwner(leader.owner, faction) ||
    hexDistance(leader.coord, unit.coord) !== 1 ||
    seat.enlisted === commanderId ||
    // VC: a leader follows nobody; it stands down first.
    unit.leadsSince !== undefined
  ) {
    return state;
  }
  return withSeat(state, { ...seat, enlisted: commanderId });
}

/** Every tick (C2), seat by seat: an enlistment whose commander has fallen or lost his badge is over. */
export function discharge(state: RunState): RunState {
  let next = state;
  for (const { id } of state.seats) {
    const seat = seatAt(next, id);
    if (seat?.enlisted !== undefined && !enlistedWith(next, seat)) {
      next = withSeat(next, without(seat, "enlisted"));
    }
  }
  return next;
}

/** Whether the seat's soldier marches with the squad it fell in with (C2): at work, unbroken, within `squadRing` of its commander. */
export function marchesWith(state: RunState, seat: SeatState, leader: Unit): boolean {
  const company = state.rules.company;
  const unit = onDuty(state, seat);
  return (
    company !== null &&
    unit !== undefined &&
    hexDistance(unit.coord, leader.coord) <= company.squadRing
  );
}

/** The seat's soldier while it is at work (C1) and unbroken: the one its King pays for a deed. */
export function onDuty(state: RunState, seat: SeatState): Unit | undefined {
  const unit = seatUnit(state, seat);
  return unit !== undefined && isAtWork(state, unit) && !isBroken(state, unit) ? unit : undefined;
}
